import React from 'react';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import {
  Avatar,
  Box,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuItem as MuiMenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  useGetIdentity,
  useLogout,
  usePermissions,
  useSidebarState,
  useTranslate,
} from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';
import { PlatformRole } from '@/access/roles';
import { useTestBenchAvailability } from '@/features/test-bench/useTestBenchAvailability';
import { isActiveRoute, navGroups, type NavEntry } from './navSections';

const icons = {
  operations: ReceiptLongOutlinedIcon,
  statistics: QueryStatsOutlinedIcon,
  tenants: ApartmentOutlinedIcon,
  applications: AppsOutlinedIcon,
  credentials: VpnKeyOutlinedIcon,
  'gateway-accounts': AccountBalanceOutlinedIcon,
  audit: FactCheckOutlinedIcon,
  'test-bench': ScienceOutlinedIcon,
} as const;

interface StyledMenuItemProps {
  open: boolean;
}

const StyledMenuItem = styled(MuiMenuItem, {
  shouldForwardProp: (prop) => prop !== 'open',
})<StyledMenuItemProps>(({ theme, open }) => ({
  position: 'relative',
  margin: theme.custom.menu.item.margin,
  padding: theme.custom.menu.item.padding,
  borderRadius: theme.custom.menu.item.borderRadius,
  color: theme.custom.menu.item.color(),
  justifyContent: open ? 'flex-start' : 'center',
  fontSize: theme.custom.menu.item.fontSize,
  '& .MuiListItemText-root': {
    minWidth: 0,
  },
  '& .MuiListItemText-primary': {
    fontSize: theme.custom.menu.item.fontSize,
    fontWeight: theme.custom.menu.item.fontWeight,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  display: 'flex',
  gap: theme.custom.menu.item.gap,
  transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',

  '& .MuiListItemIcon-root': {
    minWidth: 'auto',
    color: theme.custom.menu.item.iconColor(),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  '&:hover:not(.active)': {
    color: theme.custom.menu.item.hover.color(),
    backgroundColor: open ? theme.custom.menu.item.hover.backgroundColor() : 'transparent',
    ...(!open && {
      '& .MuiListItemIcon-root': {
        backgroundColor: theme.custom.menu.item.hover.backgroundColor(),
      },
    }),
  },

  '&.active': {
    color: open ? theme.custom.menu.item.active.color() : theme.palette.primary.main,
    backgroundColor: open ? theme.custom.menu.item.active.backgroundColor() : 'transparent',
    fontWeight: theme.custom.menu.item.active.fontWeight,
    '& .MuiListItemText-primary': {
      fontWeight: theme.custom.menu.item.active.fontWeight,
    },

    // 2px marker on the leading edge (hybrid-3c.md v2 §5.1).
    '&::before': open
      ? {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 6,
          bottom: 6,
          width: 2,
          borderRadius: '0 2px 2px 0',
          backgroundColor: theme.custom.menu.item.active.marker(),
        }
      : undefined,

    '& .MuiListItemIcon-root': {
      color: open
        ? theme.custom.menu.item.active.marker()
        : theme.custom.menu.item.closedActiveIcon.color(),
      backgroundColor: open
        ? 'transparent'
        : theme.custom.menu.item.closedActiveIcon.backgroundColor(),
      padding: open ? 0 : '6px',
      borderRadius: open ? 0 : theme.custom.menu.item.closedActiveIcon.borderRadius,
    },

    '&:hover': {
      backgroundColor: open ? theme.custom.menu.item.active.hover.backgroundColor() : 'transparent',
    },
  },
}));

const getInitials = (fullName?: string | null): string => {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  const initials = parts.length > 1 ? `${first[0] ?? ''}${last[0] ?? ''}` : first.slice(0, 2);
  return initials.toUpperCase();
};

/**
 * Side navigation as the Pattern A rail (hybrid-3c.md v3.7 + v3.9 + v2
 * §5.1): full-height, flush to the viewport edges. Carries the brand text
 * lockup at the top (the app's single "Roommatik" mention, per v3.7 — no
 * script logo here), the grouped nav entries in the middle, and the
 * profile card fixed at the bottom (moved out of the app bar — see
 * CustomAppBar — "a single place", never duplicated).
 */
export function CustomMenu() {
  const translate = useTranslate();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions } = usePermissions<PlatformRole[]>();
  const [isSidebarOpen, setSidebarOpen] = useSidebarState();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { identity } = useGetIdentity();
  const logout = useLogout();
  const [profileAnchor, setProfileAnchor] = React.useState<HTMLElement | null>(null);
  // run-6 gating: the `test-bench` entry is hidden entirely while the backend
  // flag is off (404/403), per useTestBenchAvailability — the list in
  // navSections always carries it, the gating lives here.
  const { available: testBenchAvailable } = useTestBenchAvailability();

  const isAdmin = Boolean(permissions?.includes(PlatformRole.Admin));
  const isSuperAdmin = Boolean(permissions?.includes(PlatformRole.SuperAdmin));

  const handleMenuClick = (to: string): void => {
    navigate(to);
    if (isSmallScreen) {
      setSidebarOpen(false);
    }
  };

  const handleLogout = () => {
    setProfileAnchor(null);
    logout();
  };

  const roleLabel = translate(
    isSuperAdmin
      ? 'pay.profile.roleLabelSuperAdmin'
      : isAdmin
        ? 'pay.profile.roleLabelAdmin'
        : 'pay.profile.roleLabelAuditor',
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Pattern A: the rail spans the FULL viewport height, flush to the
        // top/bottom/left edges. The AppBar is absolutely positioned over
        // the content column only (see CustomAppBar/CustomLayout), so there
        // is no app-bar strip to subtract here.
        height: '100vh',
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.menu,
        borderRight: isSmallScreen ? 'none' : `1px solid ${theme.custom.menu.border}`,
        padding: isSmallScreen ? '12px' : isSidebarOpen ? '16px 14px 16px 8px' : '16px 6px',
        width: isSmallScreen
          ? '94%'
          : isSidebarOpen
            ? theme.custom.menu.openWidth
            : theme.custom.menu.closedWidth,
        minWidth: isSmallScreen
          ? 'auto'
          : isSidebarOpen
            ? theme.custom.menu.openWidth
            : theme.custom.menu.closedWidth,
        maxWidth: isSmallScreen
          ? '100%'
          : isSidebarOpen
            ? theme.custom.menu.openWidth
            : theme.custom.menu.closedWidth,
        transition:
          'width 0.2s ease-in-out, min-width 0.2s ease-in-out, max-width 0.2s ease-in-out, padding 0.2s ease-in-out',
        overflow: 'hidden',
      }}
    >
      {(isSidebarOpen || isSmallScreen) && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', padding: '4px 8px 20px' }}>
          {/* Two-tone text lockup, white on the teal rail (v3.7 decision
              "1a") — the single place the "Roommatik" brand name appears
              inside the app; never a script logo, never repeated in the
              app bar. */}
          <Box
            component="span"
            sx={{
              fontSize: '1.15rem',
              fontWeight: 800,
              letterSpacing: '-0.01em',
              color: (t) => t.palette.common.white,
            }}
          >
            {translate('pay.appBar.wordmarkBrand')}
          </Box>
          <Box
            component="span"
            sx={{ fontSize: '1.15rem', fontWeight: 400, color: 'rgba(255,255,255,0.72)' }}
          >
            {translate('pay.appBar.wordmarkProduct')}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: isSidebarOpen || isSmallScreen ? 'auto' : 'hidden',
          overflowX: 'hidden',
          // Rail-native scrollbar: thin + translucent white (the global
          // cream scrollbar reads wrong on the teal rail).
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.22)',
            borderRadius: '999px',
          },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(255,255,255,0.35)' },
        }}
      >
        {navGroups.map((group) => (
          <Box key={group.key} sx={{ mb: 1 }}>
            {(isSidebarOpen || isSmallScreen) && (
              <Typography
                sx={{
                  fontSize: '10.5px',
                  fontWeight: 600,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  color: theme.custom.menu.groupLabelColor,
                  padding: '8px 8px 4px',
                }}
              >
                {translate(group.labelKey)}
              </Typography>
            )}
            {group.entries
              .filter((entry) => entry.key !== 'test-bench' || testBenchAvailable)
              .map((entry: NavEntry) => {
                const Icon = icons[entry.key];
                return (
                  <StyledMenuItem
                    key={entry.path}
                    onClick={() => handleMenuClick(entry.path)}
                    open={isSidebarOpen}
                    className={isActiveRoute(location.pathname, entry.path) ? 'active' : ''}
                  >
                    <ListItemIcon>
                      <Icon />
                    </ListItemIcon>
                    {(isSidebarOpen || isSmallScreen) && (
                      <ListItemText
                        primary={translate(entry.labelKey)}
                        primaryTypographyProps={{ noWrap: true }}
                      />
                    )}
                  </StyledMenuItem>
                );
              })}
          </Box>
        ))}
      </Box>

      {(isSidebarOpen || isSmallScreen) && (
        <Box
          onClick={(e) => setProfileAnchor(e.currentTarget)}
          sx={{
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '8px',
            // Translucent white overlay card, not an opaque white block —
            // stays part of the same solid-teal rail language.
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: '8px',
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
          }}
        >
          <Avatar
            sx={{
              width: 30,
              height: 30,
              fontSize: '0.75rem',
              fontWeight: 700,
              // Inverted avatar on the teal rail (hybrid-3c.md v2 §5.1):
              // white background, teal initials — not the reverse.
              backgroundColor: theme.palette.common.white,
              color: theme.palette.primary.main,
            }}
          >
            {getInitials(identity?.fullName)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              noWrap
              sx={{ fontSize: '12.5px', fontWeight: 600, color: (t) => t.palette.common.white }}
            >
              {identity?.fullName || '—'}
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: '10.5px', color: theme.custom.menu.item.iconColor() }}
            >
              {roleLabel}
            </Typography>
          </Box>
          <UnfoldMoreRoundedIcon
            sx={{ fontSize: '18px', color: theme.custom.menu.item.iconColor() }}
          />
        </Box>
      )}
      {!(isSidebarOpen || isSmallScreen) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Avatar
            onClick={(e) => setProfileAnchor(e.currentTarget)}
            sx={{
              width: 30,
              height: 30,
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'pointer',
              backgroundColor: theme.palette.common.white,
              color: theme.palette.primary.main,
            }}
          >
            {getInitials(identity?.fullName)}
          </Avatar>
        </Box>
      )}

      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
      >
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('pay.profile.logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default CustomMenu;
